import React, { useState, useMemo } from "react";
import { ShoppingBag } from "lucide-react";
import { InventoryControlPanel } from "./InventoryControlPanel";
import { SalesTable } from "./SalesTable";
import { InvoicePreviewModal } from "./InvoicePreviewModal";
import { RecordSaleReturnModal } from "./RecordSaleReturnModal";
import SaleDispatchModal from "./SalesDispatchModal";

export const SalesView = ({
  search,
  setSearch,
  filters,
  setFilters,
  dynamicSettings,
  activeTab,
  consolidatedSales,
  parties,
  setSelReq,
  setSelSale,
  setSaleOpen,
  onExport,
  isExporting,
  onRefresh
}) => {
  const [previewSale, setPreviewSale] = useState(null);
  const [returnModalSale, setReturnModalSale] = useState(null);
  const [dispatchSale, setDispatchSale] = useState(null);

  const handleRecordReturn = (sale) => {
    setPreviewSale(null); // close preview
    setReturnModalSale(sale); // open return modal
  };

  const saleTypeFilter = filters?.saleType || 'all';
  const filteredSales = useMemo(() => {
    if (saleTypeFilter === 'all') return consolidatedSales;
    return (consolidatedSales || []).filter(s => {
      const st = s.saleType || (s.recordType === 'material_request' ? 'Project Sale' : 'Direct Sale');
      return saleTypeFilter === 'project' ? st === 'Project Sale' : st === 'Direct Sale';
    });
  }, [consolidatedSales, saleTypeFilter]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <InventoryControlPanel
        activeTab={activeTab}
        search={search}
        setSearch={setSearch}
        filters={filters}
        setFilters={setFilters}
        dynamicSettings={dynamicSettings}
        parties={parties}
        partyLabel="Projects/Customers"
        showStatusFilter={false}
        showVendorFilter={false}
        showCategoryFilter={false}
        showCustomerFilter={true}
        onExport={onExport}
        isExporting={isExporting}
      >
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-blue text-slate-600 w-36"
            value={filters?.saleType || 'all'}
            onChange={e => setFilters(prev => ({ ...prev, saleType: e.target.value }))}
          >
            <option value="all">All Types</option>
            <option value="retail">Direct Sale</option>
            <option value="project">Project Sale</option>
          </select>
        </div>
      </InventoryControlPanel>
      <SalesTable
        sales={filteredSales}
        onView={(s) => {
          if (s.recordType === 'material_request') {
            setSelReq(s.originalRequest || s);
          } else {
            setPreviewSale(s);
          }
        }}
        onEdit={(s) => {
          if (s.recordType === 'sale') {
            setSelSale(s);
            setSaleOpen(true);
          }
        }}
        onRecordReturn={handleRecordReturn}
        onDispatch={(s) => setDispatchSale(s)}
      />
      {filteredSales.length === 0 && (
        <div className="card p-12 flex flex-col items-center justify-center bg-white shadow-sm border border-slate-100 rounded-2xl">
          <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mb-4">
            <ShoppingBag size={32} />
          </div>
          <h2 className="text-xl font-bold text-brand-dark mb-1">Sales Log</h2>
          <p className="text-slate-500 text-sm">Sales and Project Dispatches will appear here.</p>
        </div>
      )}
      <div className="hidden debug-data" data-sales-count={filteredSales.length}></div>

      <InvoicePreviewModal
        isOpen={!!previewSale}
        onClose={() => setPreviewSale(null)}
        sale={previewSale}
        onRecordReturn={handleRecordReturn}
      />

      <RecordSaleReturnModal 
        isOpen={!!returnModalSale}
        onClose={() => setReturnModalSale(null)}
        sale={returnModalSale}
        onReturnSuccess={() => {
          if (onRefresh) onRefresh();
          else window.location.reload();
        }}
      />
      <SaleDispatchModal
        isOpen={!!dispatchSale}
        onClose={() => setDispatchSale(null)}
        sale={dispatchSale}
        onRefresh={onRefresh}
      />
    </div>
  );
};

